import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChange,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import { IBalooUser, IBalooUserMatch } from '@fi-app/external-platform-integration/baloo/baloo';
import { PkMatchType } from '@fi-app/external-platform-integration/pk/pk-match-type';
import { ILightWeightObject } from '@fi-shared/models';
import { AppTextService } from '@fi-shared/app-text';
import { getObjectTypeNameTextId, isDefined } from '@fi-shared/utils';
import { ObjectTypes } from '@fi-shared/types';
import { FiProfileSizes } from '@fi-shared/profile-icon/profile-icon.component';
import { UserService } from '@fi-app/user/user.service';

@Component({
    selector: 'fi-baloo-match-users',
    templateUrl: './baloo-match-users.component.html',
    styleUrls: ['./baloo-match-users.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-baloo-match-users' },
    standalone: false,
})
export class BalooMatchUsersComponent implements OnChanges {
    @Input() contentShown: boolean = false;
    @Input() matches: IBalooUserMatch[] = [];
    @Input() balooUsers: IBalooUser[] = [];
    @Input() isSaveButtonDisabled: boolean = true;
    @Output() onUsersMatchSave: EventEmitter<IBalooUserMatch[]> = new EventEmitter<IBalooUserMatch[]>();
    @Output() matchChange: EventEmitter<IBalooUserMatch> = new EventEmitter<IBalooUserMatch>();
    getObjectTypeNameTextId = getObjectTypeNameTextId;

    private matchSections: { [prop: number]: IBalooUserMatch[] };
    readonly unitRoleDisplayLimit: number = 2;

    constructor(
        private appTextService: AppTextService,
        private cdr: ChangeDetectorRef,
        private userService: UserService,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        let change: SimpleChange = changes.balooUsers;
        const isChangeDifferent: (change: SimpleChange) => boolean = (change: SimpleChange) =>
            isDefined(change) && this.contentShown;
        if (isChangeDifferent(change)) this.buildMatchSections();

        change = changes.matches;
        if (isChangeDifferent(change)) this.buildMatchSections();
    }

    getUserUnitRoleLabel(arr: ILightWeightObject[]): string {
        return arr.length > this.unitRoleDisplayLimit
            ? arr[0].Name
            : arr.map((lightWeightObject: ILightWeightObject) => lightWeightObject.Name).join(', ');
    }

    isMatchesVisible(): boolean {
        return (
            !!this.getMatchSectionByType(PkMatchType.AutoMatched)?.length ||
            !!this.getMatchSectionByType(PkMatchType.PrevMatched)?.length ||
            !!this.getMatchSectionByType(PkMatchType.NotMatched)?.length
        );
    }

    getChainformationUserUnitRoleTooltipText(arr: ILightWeightObject[]): string {
        return arr
            .slice(1)
            .map((lightWeightObject: ILightWeightObject) => lightWeightObject.Name)
            .join('<br>');
    }

    getTooltipButtonText(itemsAmount: number, objectType: ObjectTypes): string {
        return `+ ${itemsAmount - 1} ${this.appTextService.get(
            getObjectTypeNameTextId(objectType, itemsAmount - 1 !== 1),
        )}`;
    }

    getMatchSectionByType(matchType: PkMatchType): IBalooUserMatch[] {
        return isDefined(this.matchSections) ? this.matchSections[matchType] : [];
    }

    isBalooUserDisabledFn(currentMatch: IBalooUserMatch, item: IBalooUser): boolean {
        return !!this.matches.find((match) => match !== currentMatch && match.BalooId === item.UserId);
    }

    saveUsersMatch() {
        this.onUsersMatchSave.emit(this.matches);
    }

    onMatchChange(match: IBalooUserMatch) {
        this.matchChange.emit(match);
    }

    private buildMatchSections() {
        // clear sections
        this.matchSections = {};
        this.matchSections[PkMatchType.AutoMatched] = [];
        this.matchSections[PkMatchType.NotMatched] = [];
        this.matchSections[PkMatchType.PrevMatched] = [];
        // fill sections
        const balooEmails = this.balooUsers.map((balooUser) => balooUser.UserEmail).join(', ');
        this.matches.forEach((match) => {
            // check if we can auto match user
            if (match.InitialMatchType === PkMatchType.NotMatched && match.UserEmailAddress && !match.BalooId) {
                const autoMatchedBalooUser = this.balooUsers.find(
                    (balooUser) =>
                        balooUser.UserEmail &&
                        balooUser.UserEmail?.toLowerCase() === match.UserEmailAddress?.toLowerCase(),
                );
                if (autoMatchedBalooUser && !this.isBalooUserDisabledFn(match, autoMatchedBalooUser)) {
                    match.InitialMatchType = PkMatchType.AutoMatched;
                    match.BalooId = autoMatchedBalooUser.UserId;
                }
            }

            this.matchSections[match.InitialMatchType].push(match);
        });
        this.cdr.markForCheck();
    }

    getMatchListCardHeading(matchType: PkMatchType, matches: IBalooUserMatch[]): string {
        return `${this.getMatchSectionLabelByType(matchType)} - ${matches.length} ${this.appTextService.get(
            getObjectTypeNameTextId(ObjectTypes.User, matches.length > 1),
        )}`;
    }

    openUserProfile(userId: number) {
        this.userService.openUserViewDialog(userId);
    }

    private getMatchSectionLabelByType(matchType: PkMatchType): string {
        switch (matchType) {
            case PkMatchType.PrevMatched:
                return this.appTextService.get('pk.matching_user_prev_matched');
            case PkMatchType.AutoMatched:
                return this.appTextService.get('pk.matching_user_auto_matched');
            case PkMatchType.NotMatched:
                return this.appTextService.get('common.not_matched');
        }
    }
    protected readonly PkMatchType = PkMatchType;
    protected readonly ObjectTypes = ObjectTypes;
    protected readonly FiProfileSizes = FiProfileSizes;

    /*
   getChainUserDataTest(match: IBalooUserMatch) {
       let matchType: PkMatchType = PkMatchType.NotMatched;
       if (this.matchSections[PkMatchType.AutoMatched].indexOf(match) !== -1) {
           matchType = PkMatchType.AutoMatched;
       }
       else if (this.matchSections[PkMatchType.PrevMatched].indexOf(match) !== -1) {
           matchType = PkMatchType.PrevMatched;
       }

       const unitNames = match.UserBusinessUnits.map(item => item.Name).join('/');
       const roleNames = match.UserRoles.map(item => item.Name).join('/');
       return `${ match.ObjectName } (${match.ObjectId} - ${match.UserEmailAddress}, ${PkMatchType[matchType]}
<br>units: ${unitNames}<br>roles: ${roleNames}
<br>activated ${match.UserIsActivated}, hasBalooRole ${match.UserHasBalooRole}`;
   }

   getBalooUserDataTest(match: IBalooUserMatch) {
       const balooUser = this.balooUsers.find(item => item.UserId == match.BalooId);
       if (!balooUser)
           return '';
       const managerGroupNames = (balooUser.ManagerGroups ?? []).map(item => item.GroupName).join('/');
       const memberGroupNames = (balooUser.MemberGroups ?? []).map(item => item.GroupName).join('/');
       return `${balooUser.UserName} (${balooUser.UserEmail}), ${balooUser.UserId}<br>Manager in ${managerGroupNames}, Member in ${memberGroupNames}`
   }

   get matchesTest() { // for tests
       return this.matchSections[PkMatchType.AutoMatched]
           .concat(this.matchSections[PkMatchType.NotMatched])
           .concat(this.matchSections[PkMatchType.PrevMatched]);
   }
    */
}
